import { Box, IconButton, makeStyles, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import SignatureCanvas from "react-signature-canvas";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalDialog from "../../../components/ModalDialog";
import { Delete } from "@material-ui/icons";
import ImageUpload from "./ImageUpload";
import { ImageType } from "./ImageEdit";
import usePersonalInfo from "./use-personal-info";
import { useDataMutation } from "../../../hooks/useDataModule";
import { UPDATE_PROFILE_PICTURE_OR_SIGNATURE } from "../queries";
import { useSetUserFromUserDataProps } from "../../../store/user";
import { BlobContainerType } from "../../../models/generated/graphql";
interface SignatureModalProps {
	isOpen: boolean;
	isUploadedImagePresent: boolean;
	isUserDrawSignaturePresent: boolean;
	setIsUploadedImagePresent: (state: boolean) => void;
	setIsSaveEnabled?: (isEnabled: boolean) => void;
	closeModal: () => void;
	setIsUserDrawSignaturePresent: (state: boolean) => void;
}

export enum CreationWay {
	UPLOAD = "upload",
	DRAW = "draw",
}

const SignatureDraw = forwardRef((props: Omit<SignatureModalProps, "isOpen" | "closeModal">, ref) => {
	const setUser = useSetUserFromUserDataProps();
	const [personalInfo] = usePersonalInfo();
	const pad = useRef<SignatureCanvas | null>(null);
	const [handleSave] = useDataMutation(UPDATE_PROFILE_PICTURE_OR_SIGNATURE);
	const sourceImage = props.isUserDrawSignaturePresent && personalInfo.isSignatureCreationDraw ? personalInfo.sourceUserSignature : null;
	useImperativeHandle(
		ref,
		() => ({
			async saveImage() {
				await onSave();
			},
		}),
		[]
	);
	const onSave = async () => {
		const blobName = personalInfo.id + "-signature-" + CreationWay.DRAW;
		const blobNameSource = personalInfo.id + "-signature-source-" + CreationWay.DRAW;
		const image = pad.current?.getTrimmedCanvas().toDataURL("image/png");

		if (image) {
			handleSave({
				variables: {
					userProfileOrSignatureInput: {
						blobContainerType: BlobContainerType.Signature,
						blobName: blobName,
						content: image,
						blobNameSource: blobNameSource,
						contentSource: image,
					},
				},
			});

			setUser({
				...personalInfo,
				userSignature: image,
				sourceUserSignature: image,
				isSignatureCreationDraw: true,
			});

			props.setIsUserDrawSignaturePresent(true);
		}
	};

	const clearPad = () => {
		drawSupportLine();
		props.setIsSaveEnabled && props.setIsSaveEnabled(false);
	};

	const drawSupportLine = (image?: CanvasImageSource) => {
		if (!pad.current) return;

		const canvas = pad.current.getCanvas();
		const canvasContext = canvas.getContext("2d")!;
		pad.current.clear();
		image && canvasContext.drawImage(image, 0, 0);
		canvasContext.beginPath();
		canvasContext.moveTo(20, 80);
		canvasContext.lineTo(260, 80);
		canvasContext.strokeStyle = "#cddcdc";
		canvasContext.stroke();
	};

	useEffect(() => {
		const image = new Image();
		if (sourceImage) {
			image.src = sourceImage;
		}
		drawSupportLine(image);
	}, [pad]);

	return (
		<Box
			display="flex"
			justifyContent="space-around"
			paddingTop="90px"
			style={{ position: "relative", width: "441px", height: "293px", margin: "8px 64px", backgroundColor: "#f5f8f9" }}
		>
			<SignatureCanvas
				penColor="black"
				backgroundColor="#ffffff"
				canvasProps={{ width: 280, height: 120, style: { position: "absolute" }, className: "sigCanvas" }}
				onBegin={(e) => e.stopPropagation()}
				onEnd={(e) => {
					e.stopPropagation();
					props.setIsSaveEnabled && props.setIsSaveEnabled(true);
				}}
				ref={(ref) => (pad.current = ref)}
			/>
			<IconButton style={{ position: "absolute", right: "90px", top: "100px" }} onClick={clearPad} color="primary">
				<Delete fontSize="small" />
			</IconButton>
		</Box>
	);
});

const SignatureModal = (props: SignatureModalProps) => {
	const [isUpload, setIsUpload] = useState<boolean>(false);
	const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(false);
	const [personalInfo] = usePersonalInfo();
	const classes = useStyles();
	const { t } = useTranslation();
	const uploader = useRef<any>(null);
	const signPad = useRef<any>(null);
	const toggleUploadView = (isUpload: boolean) => {
		if (personalInfo.isSignatureCreationDraw) {
			props.setIsUploadedImagePresent(false);
		}
		setIsSaveEnabled(false);
		setIsUpload(isUpload);
	};

	const modalText = {
		discard: "cancel",
		continue: "save",
	};

	const saveImage = async () => {
		if (uploader.current && isUpload) await uploader.current.saveImage();
		if (signPad.current && !isUpload) await signPad.current.saveImage();

		props.closeModal();
		setIsSaveEnabled(false);
	};

	const closeModal = () => {
		props.closeModal();
		props.setIsUploadedImagePresent(props.isUploadedImagePresent && !!personalInfo.sourceUserSignature);
		setIsSaveEnabled(false);
	};

	return (
		<>
			<ModalDialog
				isOpen={props.isOpen}
				onContinueClick={saveImage}
				onDiscardClick={closeModal}
				isSaveEnabled={isSaveEnabled}
				text={modalText}
				dialogActionStyles={{ marginRight: "38px" }}
			>
				<Box display="flex" flexDirection="column" width="100%" height="100%">
					<Box display="flex" flexDirection="row" marginBottom={4} marginLeft={4.8} marginTop={"-22px"}>
						<Button className={classes.resetButton} onClick={() => toggleUploadView(false)}>
							<Typography className={`${classes.typographyBtn} ${!isUpload && classes.activeBtn}`}>{t("draw")}</Typography>
						</Button>
						<Button className={classes.resetButton} onClick={() => toggleUploadView(true)}>
							<Typography className={`${classes.typographyBtn} ${isUpload && classes.activeBtn}`}>{t("upload")}</Typography>
						</Button>
					</Box>
					{isUpload ? (
						<Box marginX={8} marginY={1}>
							<ImageUpload
								ref={uploader}
								imageType={ImageType.SIGNATURE}
								onDiscardClick={() => {}}
								setIsImagePresent={props.setIsUploadedImagePresent}
								setSaveEnabled={setIsSaveEnabled}
								isImagePresent={props.isUploadedImagePresent}
								areButtonsDisabled={true}
								paperStyles={{ width: "441px", height: props.isUploadedImagePresent ? "272px" : "293px" }}
							/>
						</Box>
					) : (
						<SignatureDraw
							ref={signPad}
							setIsUploadedImagePresent={props.setIsUploadedImagePresent}
							isUploadedImagePresent={props.isUploadedImagePresent}
							isUserDrawSignaturePresent={props.isUserDrawSignaturePresent}
							setIsSaveEnabled={setIsSaveEnabled}
							setIsUserDrawSignaturePresent={props.setIsUserDrawSignaturePresent}
						/>
					)}
				</Box>
			</ModalDialog>
		</>
	);
};

export default SignatureModal;

const useStyles = makeStyles({
	resetButton: {
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
			backgroundColor: "transparent",
		},
	},
	typographyBtn: {
		fontSize: "22px",
		fontWeight: 300,
		color: "#969392",
	},
	activeBtn: {
		color: "#367bf5",
		fontWeight: 600,
	},
});
